import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { getApiBaseUrl } from '../../config/apiBase';
import { 
  Download, 
  Search, 
  Filter, 
  RefreshCw,
  FileText,
  Server,
  Activity,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Cpu,
  ChevronDown,
  Eye,
  Trash2,
  Calendar,
  AlertTriangle,
  Info,
  Terminal,
  Play,
  Pause,
  ArrowDown,
  ArrowUp
} from 'lucide-react';
import SearchInput from '../../components/common/SearchInput';

const WorkerLogs = () => {
  const [workers, setWorkers] = useState([]);
  const [filteredWorkers, setFilteredWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('ALL');
  const [realtimeMode, setRealtimeMode] = useState(false);
  const refreshInterval = useRef(null);
  const logsContainerRef = useRef(null);
  const userScrolled = useRef(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  useEffect(() => {
    fetchWorkers();
  }, []);

  // Filter workers based on search
  useEffect(() => {
    let filtered = workers;
    
    if (searchTerm) {
      filtered = filtered.filter(worker => 
        worker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        worker.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredWorkers(filtered);
  }, [workers, searchTerm]);

  // Scroll to top when logs change to show newest entries
  useEffect(() => {
    if (logsContainerRef.current && logs.length > 0 && !userScrolled.current) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        if (logsContainerRef.current && !userScrolled.current) {
          logsContainerRef.current.scrollTop = 0;
        }
      }, 100);
    }
  }, [logs]);

  // Reset user scroll when selecting new worker
  useEffect(() => {
    userScrolled.current = false;
  }, [selectedWorker]);

  useEffect(() => {
    // Setup auto refresh
    if (autoRefresh && selectedWorker && !isPaused) {
      refreshInterval.current = setInterval(() => {
        fetchLogs(selectedWorker.code, true);
      }, 2000);
    } else {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    }

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [autoRefresh, selectedWorker, isPaused]);

  const fetchWorkers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWorkers(data || []);
      } else if (response.status === 403) {
        toast.error('Access denied. Master Admin role required.');
      } else {
        toast.error('Failed to fetch workers');
      }
    } catch (error) {
      console.error('Error fetching workers:', error);
      console.error('Response details:', error.response);
      toast.error('Failed to fetch workers');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async (workerCode, isRefresh = false) => {
    if (!workerCode) return;
    
    if (!isRefresh) {
      setLogsLoading(true);
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers/${workerCode}/logs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const newLogs = data.logs || [];
        
        // Backend already returns logs in DESC order (newest first)
        // No need to reverse
        
        if (isRefresh && realtimeMode) {
          // Prepend new logs in realtime mode (newest first)
          setLogs(prev => {
            const existingTimestamps = new Set(prev.map(log => log.timestamp));
            const filteredNewLogs = newLogs.filter(log => !existingTimestamps.has(log.timestamp));
            return [...filteredNewLogs, ...prev].slice(0, 500); // Keep first 500 logs
          });
        } else {
          setLogs(newLogs);
        }
      } else {
        toast.error('Failed to fetch worker logs');
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to fetch worker logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleSelectWorker = (worker) => {
    setSelectedWorker(worker);
    setLogs([]);
    setIsPaused(false);
    fetchLogs(worker.code);
  };

  const handleRefresh = () => {
    if (selectedWorker) {
      fetchLogs(selectedWorker);
    }
  };

  const handleClearLogs = () => {
    setLogs([]);
    toast.success('Logs cleared');
  };

  const handleDownloadLogs = () => {
    const logsText = logs.map(log => 
      `[${log.timestamp}] ${log.level}: ${log.message}`
    ).join('\n');
    
    const blob = new Blob([logsText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedWorker || 'worker'}_logs_${new Date().toISOString()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const scrollToBottom = () => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
      userScrolled.current = true; // User initiated scroll
    }
  };

  const handleScroll = () => {
    if (logsContainerRef.current) {
      const { scrollTop } = logsContainerRef.current;
      // Mark user as scrolled if they're not at the top
      userScrolled.current = scrollTop > 50;
      // Show jump button if not at top
      setShowJumpToLatest(scrollTop > 100);
    }
  };

  const jumpToLatest = () => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = 0;
      userScrolled.current = false;
      setShowJumpToLatest(false);
    }
  };

  const handleStartWorker = async (workerCode) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers/${workerCode}/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        toast.success('Worker started successfully');
        fetchWorkers();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to start worker');
      }
    } catch (error) {
      console.error('Error starting worker:', error);
      toast.error('Failed to start worker');
    }
  };

  const handleStopWorker = async (workerCode) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers/${workerCode}/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        toast.success('Worker stopped successfully');
        fetchWorkers();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to stop worker');
      }
    } catch (error) {
      console.error('Error stopping worker:', error);
      toast.error('Failed to stop worker');
    }
  };

  const getFilteredLogs = () => {
    let filtered = logs;
    
    // Filter by level
    if (filterLevel !== 'ALL') {
      filtered = filtered.filter(log => log.level === filterLevel);
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.timestamp.includes(searchTerm)
      );
    }
    
    return filtered;
  };

  const formatLogTime = (timestamp) => {
      try {
        const d = new Date(timestamp);
        if (isNaN(d.getTime())) return 'N/A';
        return d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      } catch (error) {
        return 'N/A';
      }
    };

  const getLogIcon = (level) => {
    switch (level?.toUpperCase()) {
      case 'ERROR':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'WARN':
      case 'WARNING':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'INFO':
        return <CheckCircle className="w-4 h-4 text-blue-600" />;
      default:
        return <CheckCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getLogLevelIcon = (level) => {
    switch (level?.toUpperCase()) {
      case 'ERROR':
        return <XCircle className="w-4 h-4" />;
      case 'WARN':
      case 'WARNING':
        return <AlertTriangle className="w-4 h-4" />;
      case 'INFO':
        return <Info className="w-4 h-4" />;
      case 'DEBUG':
        return <Terminal className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getLogLevelColor = (level) => {
    switch (level) {
      case 'ERROR':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'WARN':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'INFO':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'DEBUG':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Terminal className="w-8 h-8 text-indigo-600" />
              Worker Logs
            </h1>
            <p className="text-gray-600 mt-2">View and monitor worker logs in real-time</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadLogs}
              disabled={!logs.length}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors duration-200 shadow-sm"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing || !selectedWorker}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors duration-200 shadow-sm"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Worker Selection and Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Worker</label>
              <select
                value={selectedWorker || ''}
                onChange={(e) => {
                  const code = e.target.value;
                  if (code) {
                    setSelectedWorker(code);
                    setLogs([]);
                    setIsPaused(false);
                    fetchLogs(code);
                  } else {
                    setSelectedWorker(null);
                    setLogs([]);
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              >
                <option value="">Choose a worker...</option>
                {workers.map((worker) => (
                  <option key={worker.code} value={worker.code}>
                    {worker.name} ({worker.code})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Auto-refresh</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={realtimeMode}
                  onChange={(e) => setRealtimeMode(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Real-time</span>
              </label>
              
              <button
                onClick={() => setIsPaused(!isPaused)}
                disabled={!selectedWorker}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors"
              >
                {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                {isPaused ? 'Resume' : 'Pause'}
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search workers..."
            className="flex-1"
          />
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
            >
              <option value="ALL">All Levels</option>
              <option value="ERROR">Errors</option>
              <option value="WARNING">Warnings</option>
              <option value="INFO">Info</option>
              <option value="DEBUG">Debug</option>
            </select>
          </div>
        </div>
      </div>

      {/* Logs Display */}
      {selectedWorker ? (
        <div className="bg-gray-900 rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gray-800 px-6 py-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Terminal className="w-5 h-5 text-green-400" />
                <span className="text-white font-medium">
                  {workers.find(w => w.code === selectedWorker)?.name} Logs
                </span>
                {realtimeMode && (
                  <span className="px-2 py-1 text-xs bg-green-600 text-white rounded-full animate-pulse">
                    LIVE
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={scrollToBottom}
                  className="flex items-center gap-2 px-3 py-1 text-sm text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                  title="Scroll to oldest logs"
                >
                  <ArrowDown className="w-4 h-4" />
                  Scroll to Oldest
                </button>
                <button
                  onClick={jumpToLatest}
                  className={`flex items-center gap-2 px-3 py-1 text-sm rounded transition-all ${
                    showJumpToLatest 
                      ? 'bg-blue-600 text-white hover:bg-blue-700 opacity-100' 
                      : 'bg-gray-200 text-gray-400 opacity-0 pointer-events-none'
                  }`}
                >
                  <ArrowUp className="w-4 h-4" />
                  Jump to Latest
                </button>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Activity className="w-4 h-4" />
                  {logs.length} entries
                </div>
              </div>
            </div>
          </div>
          
          <div ref={logsContainerRef} onScroll={handleScroll} className="h-[600px] overflow-y-auto p-6 font-mono text-sm">
            {logsLoading ? (
              <div className="flex items-center justify-center h-full">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-500" />
              </div>
            ) : logs.length > 0 ? (
              <div className="space-y-2">
                {logs.map((log, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-3 p-3 rounded-lg ${
                      filterLevel === 'ALL' || log.level === filterLevel
                        ? 'opacity-100'
                        : 'opacity-30'
                    }`}
                  >
                    <span className="text-gray-500 text-xs flex-shrink-0 mt-0.5">
                      {formatLogTime(log.timestamp)}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full border flex items-center gap-1 ${getLogLevelColor(log.level)}`}>
                      {getLogLevelIcon(log.level)}
                      {log.level}
                    </span>
                    <span className="text-gray-300 flex-1 break-all">
                      {log.message}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No logs available</p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Terminal className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Worker Selected</h3>
          <p className="text-gray-600">Select a worker to view its logs</p>
        </div>
      )}
    </div>
  );
};

export default WorkerLogs;
